import { cn } from "@/utils/theme";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  inputVariants,
  ScrollArea,
} from "@elaniintc/goes-design-system";
import { type IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import { DropdownListSeparator } from "@/components/ui/dropdown-list/dropdown-list-separator";
import type { ComponentProps } from "react";
import { useState } from "react";

interface ExtendedDropdownListItem extends IDropdownListItem {
  type?: string;
  selected?: boolean;
}

interface DropdownListGroup {
  groupId: string;
  groupLabels: string[];
  items: ExtendedDropdownListItem[];
  icon?: ComponentProps<typeof Icon> & {
    variant?:
      | "info"
      | "warning"
      | "disabled"
      | "action"
      | "success"
      | "danger"
      | "neutral"
      | null
      | undefined;
  };
}

interface MultiSelectDropdownProps {
  options: DropdownListGroup[];
  placeholder?: string;
  selectedItems: string[]; // Array of selected IDs.
  onSelectionChange: (selectedIds: string[]) => void;
  disabled?: boolean;
  search?: {
    placeholder: string;
    query: string;
    onSearch: (query: string) => void;
  };
}

export const MultiSelectDropdown = ({
  options,
  placeholder = "Seleccionar elementos...",
  selectedItems,
  onSelectionChange,
  disabled,
  search,
}: MultiSelectDropdownProps) => {
  const [open, setOpen] = useState(false);

  const handleItemClick = (item: ExtendedDropdownListItem) => {
    const isSelected = selectedItems.includes(item.value);
    let newSelection: string[];

    if (isSelected) {
      // Remove from selection
      newSelection = selectedItems.filter((id) => id !== item.value);
    } else {
      // Add to selection
      newSelection = [...selectedItems, item.value];
    }

    onSelectionChange(newSelection);
    // Don't close dropdown for multiselect
  };

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    }

    // Buscar todos los labels de los items seleccionados
    const labels: string[] = [];

    for (const value of selectedItems) {
      for (const group of options) {
        const item = group.items.find((i) => i.value === value);
        if (item) labels.push(item.label as string);
      }
    }

    // Si encontró labels, mostrarlos separados por coma
    if (labels.length > 0) {
      return labels.join(", ");
    }

    // Fallback
    return `${selectedItems.length} elementos seleccionados`;
  };

  // Add selected state to items
  const optionsWithSelection = options.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      selected: selectedItems.includes(item.value),
    })),
  }));

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen && search) {
          search.onSearch("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled}
          variant="tertiary"
          className={cn(
            "flex justify-start items-center group min-w-0 relative gap-2",
            inputVariants({
              className: cn(
                "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                "w-full",
              ),
            }),
          )}
        >
          {selectedItems.length > 0 && (
            <Badge shape="pill" size="xs" variant="action">
              {selectedItems.length}
              <Icon type="far" size="xs" name="file" />
            </Badge>
          )}
          <p
            className={cn(
              "truncate font-normal pr-5 flex-1 text-left",
              selectedItems.length > 0 ? "text-body" : "text-body-quiet",
            )}
          >
            {getDisplayText()}
          </p>

          <Icon
            type="far"
            name="angle-down"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-body group-data-[state=open]:rotate-180 transition-transform"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl w-[var(--radix-dropdown-menu-trigger-width)]",
        )}
        side="bottom"
        align="start"
      >
        {search && (
          <div className="border-b border-neutral-quiet p-3 relative ">
            <Icon
              type="far"
              name="search"
              className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
            />
            <Input
              variant="sm"
              className="text-sm w-full font-normal flex-auto pl-9"
              placeholder={search.placeholder || "Buscar..."}
              onChange={(e) => search.onSearch(e.target.value)}
              value={search.query}
            />
          </div>
        )}
        <ScrollArea className="max-h-66 p-2">
          {optionsWithSelection.map((group) => (
            <div key={group.groupId} className="flex flex-col gap-2 mb-2">
              <DropdownListSeparator
                options={group.groupLabels}
                icon={group.icon}
              />
              {group.items.map((item) => {
                return (
                  <div
                    key={item.value}
                    role="menuitem"
                    className="rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="w-full flex items-center justify-between gap-2 min-w-0">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {item.icon && (
                          <Icon {...item.icon} className="flex-shrink-0" />
                        )}
                        <div className="flex gap-2 min-w-0 flex-1">
                          <p className="font-semibold text-body truncate">
                            {item.label}
                          </p>
                          {item.type && (
                            <span className="font-normal text-body-quiet truncate">
                              {item.type}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Checkbox indicator */}
                      <div className="flex items-center flex-shrink-0">
                        <Checkbox
                          checked={selectedItems.includes(item.value)}
                          onChange={() => {}}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {options.length === 0 && (
            <div className="p-4 text-center text-body-quiet">
              {search?.query
                ? `No se encontraron resultados para "${search.query}"`
                : "No hay elementos disponibles"}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
